<script setup>
  import { ref, onMounted, watch } from "vue";
  import { RouterLink, useRoute } from "vue-router";
  import { UserPlus, X } from "lucide-vue-next";
  import { useStore } from "vuex";

  // Vuex store and route access
  const store = useStore();
  const route = useRoute();
  const visible = ref(true);
  const goClickCount = ref(0); // 🧮 Track number of times clicked

  // Emit event to parent
  const emit = defineEmits(["goClicked"]);

  // Fetch total students on mount
  onMounted(async () => {
    await store.dispatch("fetchTotalTeacherCountOnboarding");
  });

  // Hide banner if students exist
  watch(
    () => store.state.onboardingTotalTeachers,
    (newVal) => {
      if (newVal >= 1) visible.value = false;
    },
    { immediate: true }
  );

  // Close banner manually
  function close() {
    visible.value = false;
  }

  // Handle "Go" button click
  function handleGoClick(event) {
    goClickCount.value++; // increase count
    if (route.path === "/students") {
      // Prevent redundant navigation
      event.preventDefault();
      emit("goClicked", { clicked: true, count: goClickCount.value });
    } else {
      emit("goClicked", { clicked: false, count: goClickCount.value });
    }
  }
</script>

<template>
  <!-- Global top banner -->
  <transition
    name="banner-slide"
    appear
  >
    <div
      v-if="visible"
      class="fixed top-24 md:top-28 z-50 w-full flex justify-center px-2 sm:px-4 left-[2px] md:left-[600px]"
    >
      <div
        class="w-full max-w-2xl flex items-center justify-between gap-2 sm:gap-3 rounded-xl shadow-lg border border-emerald-400 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-400 text-white animate-gradient-x px-3 sm:px-5 py-3 sm:py-3"
        role="region"
        aria-label="Onboarding banner"
      >
        <!-- Left: Icon + Text -->
        <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <UserPlus class="h-5 w-5 animate-bounce-slow flex-shrink-0" />
          <p class="text-sm sm:text-base font-medium whitespace-nowrap">
            Create your first teacher to get started!
          </p>
        </div>

        <!-- Right: Buttons -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <RouterLink
            @click="handleGoClick"
            to="/teacher"
            class="inline-flex items-center rounded-full bg-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-emerald-700 shadow-md hover:shadow-lg hover:bg-emerald-50 transition-all duration-300 whitespace-nowrap"
            >Go</RouterLink
          >

          <button
            type="button"
            @click="close"
            class="rounded-full p-2 hover:bg-emerald-500/20 transition-all duration-200 hover:rotate-90"
            aria-label="Dismiss banner"
          >
            <X
              class="h-4 w-4 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
            <span class="sr-only">Dismiss</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
  /* 🌈 Slide-in Animation */
  .banner-slide-enter-active,
  .banner-slide-leave-active {
    transition: all 0.4s ease;
  }
  .banner-slide-enter-from {
    opacity: 0;
    transform: translateY(-20px);
  }
  .banner-slide-leave-to {
    opacity: 0;
    transform: translateY(-20px);
  }

  /* 🔁 Animated gradient */
  @keyframes gradient-x {
    0%,
    100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }
  .animate-gradient-x {
    background-size: 200% 200%;
    animation: gradient-x 6s ease infinite;
  }

  /* 🫧 Slow bounce for icon */
  @keyframes bounce-slow {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-3px);
    }
  }
  .animate-bounce-slow {
    animation: bounce-slow 2s infinite;
  }
</style>
